/**
 * La descripción del puesto en «Habilidades mínimas».
 *
 * Al elegir un puesto del catálogo, su descripción se pone en el campo de
 * habilidades para que quien captura parta de ella y no de una hoja en blanco.
 * El campo es un editor de texto enriquecido (guarda HTML) y la descripción del
 * catálogo es texto plano, así que hay que convertirla antes de pasarla.
 */

const ETIQUETA_HTML = /<\/?[a-z][^>]*>/i

const escapar = (texto: string): string =>
  texto
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

/** Texto plano → párrafos. Lo que ya viene como HTML se respeta tal cual. */
export const descripcionComoHtml = (descripcion?: string | null): string => {
  const texto = (descripcion ?? '').trim()
  if (!texto) return ''
  if (ETIQUETA_HTML.test(texto)) return texto

  return texto
    .split(/\r?\n+/)
    .map((linea) => linea.trim())
    .filter(Boolean)
    .map((linea) => `<p>${escapar(linea)}</p>`)
    .join('')
}

/**
 * El editor vacío devuelve `<p></p>`, no una cadena vacía: sin esto un campo
 * sin contenido parece lleno.
 */
export const esHtmlVacio = (html?: string | null): boolean =>
  !(html ?? '')
    .replace(/<br\s*\/?>/gi, '')
    .replace(/<\/?[a-z][^>]*>/gi, '')
    .replace(/&nbsp;/gi, ' ')
    .trim()

/**
 * Qué poner en Habilidades mínimas al elegir un puesto, o `null` si no hay que
 * tocar nada.
 *
 * Se reemplaza solo cuando el campo está vacío o todavía trae lo que se puso
 * solo con el puesto anterior. Si la persona ya escribió o corrigió algo, no se
 * le pisa: perder lo capturado es peor que no autollenar.
 */
export const habilidadesAlElegirPuesto = ({
  actual,
  ultimoAutollenado,
  descripcionDelPuesto,
}: {
  actual: string
  ultimoAutollenado: string
  descripcionDelPuesto?: string | null
}): string | null => {
  const sinTocar =
    esHtmlVacio(actual) ||
    (ultimoAutollenado !== '' && actual === ultimoAutollenado)
  if (!sinTocar) return null

  const nuevo = descripcionComoHtml(descripcionDelPuesto)
  return nuevo === actual ? null : nuevo
}
